import {
  deleteV1CompaniesCompanyIdOvertimeRulesId,
  getV1CompaniesCompanyIdOvertimeRules,
  patchV1CompaniesCompanyIdOvertimeRulesId,
  postV1CompaniesCompanyIdOvertimeRules,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { OvertimeRule, OvertimeRuleFormData } from '~hooks/useOvertimeRule/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

export default function useOvertimeRule() {
  const getOvertimeRules = useCallback(
    async (companyId: string, link?: PaginationLink, cursor?: string) => {
      try {
        const response = await getV1CompaniesCompanyIdOvertimeRules({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
        });

        return {
          data: response.data.data.map(OvertimeRule.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.overtime_rules.failed_to_fetch'),
        );
        throw error;
      }
    },
    [],
  );

  const createOvertimeRule = useCallback(
    async (companyId: string, data: OvertimeRuleFormData) => {
      try {
        const response = await postV1CompaniesCompanyIdOvertimeRules({
          path: { 'company-id': companyId },
          body: OvertimeRule.deparse(data),
        });

        return OvertimeRule.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.overtime_rules.failed_to_create'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteOvertimeRule = useCallback(
    async (companyId: string, overtimeRuleId: string) => {
      try {
        await deleteV1CompaniesCompanyIdOvertimeRulesId({
          path: { 'company-id': companyId, id: overtimeRuleId },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.overtime_rules.failed_to_delete'),
        );
        throw error;
      }
    },
    [],
  );

  const updateOvertimeRule = useCallback(
    async (companyId: string, overtimeRuleId: string, data: OvertimeRuleFormData) => {
      try {
        const response = await patchV1CompaniesCompanyIdOvertimeRulesId({
          path: { 'company-id': companyId, id: overtimeRuleId },
          body: OvertimeRule.deparseUpdate(data),
        });

        return OvertimeRule.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.overtime_rules.failed_to_update'),
        );
        throw error;
      }
    },
    [],
  );

  return { createOvertimeRule, deleteOvertimeRule, getOvertimeRules, updateOvertimeRule };
}
